import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container, Row, Col, Card, Button, Form, Modal, Spinner, Alert, Badge, Table,
} from 'react-bootstrap';
import {
  FaUpload, FaTh, FaList, FaDownload, FaTrash, FaEdit, FaFilePdf, FaFileWord,
  FaFileExcel, FaFileImage, FaFileAlt, FaFileArchive, FaSearch,
} from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import { api } from '../../../contexts/AuthContext';

const ACCENT = '#60A5FA';

const CATEGORIES = [
  { value: 'contract', label: 'Contracts', hint: 'Signed agreements' },
  { value: 'offer', label: 'Offers & proposals', hint: 'Quotes you sent or received' },
  { value: 'template', label: 'Templates', hint: 'Blank forms, letterheads' },
  { value: 'nda', label: 'NDAs & legal', hint: 'Confidentiality, terms' },
  { value: 'certificate', label: 'Certificates & licenses', hint: 'GEMI, tax, insurance' },
  { value: 'company_papers', label: 'Company papers', hint: 'Statutes, IDs, bank letters' },
  { value: 'invoice_scan', label: 'Invoice scans', hint: 'PDF copies of invoices' },
  { value: 'other', label: 'Other', hint: 'Everything else' },
];

const fileIcon = (ext, mime) => {
  const e = (ext || '').toLowerCase();
  const m = (mime || '').toLowerCase();
  if (e === 'pdf' || m.includes('pdf')) return <FaFilePdf style={{ color: '#f87171' }} />;
  if (['doc', 'docx', 'odt', 'rtf'].includes(e) || m.includes('word')) {
    return <FaFileWord style={{ color: '#60A5FA' }} />;
  }
  if (['xls', 'xlsx', 'csv'].includes(e) || m.includes('sheet') || m.includes('excel')) {
    return <FaFileExcel style={{ color: '#22c55e' }} />;
  }
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(e) || m.startsWith('image/')) {
    return <FaFileImage style={{ color: '#fbbf24' }} />;
  }
  if (['zip', 'rar', '7z'].includes(e) || m.includes('zip')) {
    return <FaFileArchive style={{ color: '#a78bfa' }} />;
  }
  return <FaFileAlt style={{ color: '#9ca3af' }} />;
};

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [dragging, setDragging] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'contract',
    company: '',
    file: null,
  });

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (category) params.category = category;
      if (search.trim()) params.search = search.trim();
      const [docsRes, companiesRes] = await Promise.all([
        api.get('/api/documents/', { params }),
        api.get('/api/companies/').catch(() => ({ data: [] })),
      ]);
      setDocuments(docsRes.data);
      setCompanies(companiesRes.data || []);
    } catch (err) {
      console.error(err);
      setError('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, [category, search]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchDocuments();
    }, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchDocuments, search]);

  const openUpload = (file = null) => {
    const name = file?.name || '';
    setUploadForm({
      title: name ? name.replace(/\.[^.]+$/, '') : '',
      description: '',
      category: category || 'contract',
      company: '',
      file: file || null,
    });
    setShowUpload(true);
  };

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length === 1) {
      openUpload(files[0]);
      return;
    }
    // Multi-upload quickly with defaults
    uploadMany(files);
  };

  const uploadMany = async (files) => {
    setUploading(true);
    let ok = 0;
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('title', file.name.replace(/\.[^.]+$/, '') || file.name);
        fd.append('category', category || 'other');
        fd.append('description', '');
        await api.post('/api/documents/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        ok += 1;
      }
      toast.success(`${ok} file${ok === 1 ? '' : 's'} uploaded`);
      await fetchDocuments();
    } catch (err) {
      console.error(err);
      toast.error(ok ? `Uploaded ${ok}, then failed` : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Choose a file');
      return;
    }
    const fd = new FormData();
    fd.append('file', uploadForm.file);
    fd.append('title', uploadForm.title.trim() || uploadForm.file.name);
    fd.append('category', uploadForm.category);
    fd.append('description', uploadForm.description.trim());
    if (uploadForm.company) fd.append('company', uploadForm.company);

    try {
      setUploading(true);
      await api.post('/api/documents/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Uploaded');
      setShowUpload(false);
      await fetchDocuments();
    } catch (err) {
      console.error(err);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      const fd = new FormData();
      fd.append('title', editing.title.trim());
      fd.append('category', editing.category);
      fd.append('description', (editing.description || '').trim());
      if (editing.company) fd.append('company', editing.company);
      else fd.append('company', '');
      if (editing.newFile) fd.append('file', editing.newFile);

      await api.patch(`/api/documents/${editing.id}/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Updated');
      setShowEdit(false);
      setEditing(null);
      await fetchDocuments();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete “${doc.title}”?`)) return;
    try {
      await api.delete(`/api/documents/${doc.id}/`);
      toast.success('Deleted');
      await fetchDocuments();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  // Load unfiltered counts for sidebar
  const [allDocs, setAllDocs] = useState([]);
  useEffect(() => {
    api.get('/api/documents/').then((res) => setAllDocs(res.data)).catch(() => {});
  }, [documents.length]);

  const sidebarCounts = useMemo(() => {
    const map = { all: allDocs.length };
    CATEGORIES.forEach((c) => {
      map[c.value] = allDocs.filter((d) => d.category === c.value).length;
    });
    return map;
  }, [allDocs]);

  return (
    <Container fluid className="mt-3 px-3">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h2 style={{ color: ACCENT, marginBottom: 4 }}>Documents</h2>
          <div style={{ color: '#9ca3af', fontSize: 13 }}>
            Contracts · offers · templates · legal — your file drive
          </div>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setViewMode('grid')}
            style={viewMode === 'grid' ? { borderColor: ACCENT, color: ACCENT } : undefined}
          >
            <FaTh />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setViewMode('list')}
            style={viewMode === 'list' ? { borderColor: ACCENT, color: ACCENT } : undefined}
          >
            <FaList />
          </Button>
          <Button
            style={{ backgroundColor: ACCENT, borderColor: ACCENT }}
            onClick={() => openUpload()}
            disabled={uploading}
          >
            <FaUpload className="me-2" /> Upload
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      <Row className="g-3">
        <Col md={3} lg={2}>
          <Card className="bg-dark text-light border-secondary">
            <Card.Body className="p-2">
              <Button
                variant="link"
                className="w-100 text-start text-decoration-none mb-1"
                style={{
                  color: !category ? ACCENT : '#d1d5db',
                  background: !category ? '#1f2937' : 'transparent',
                  borderRadius: 6,
                }}
                onClick={() => setCategory('')}
              >
                All files
                <Badge bg="secondary" className="float-end">{sidebarCounts.all || 0}</Badge>
              </Button>
              {CATEGORIES.map((c) => (
                <Button
                  key={c.value}
                  variant="link"
                  className="w-100 text-start text-decoration-none mb-1"
                  style={{
                    color: category === c.value ? ACCENT : '#d1d5db',
                    background: category === c.value ? '#1f2937' : 'transparent',
                    borderRadius: 6,
                  }}
                  onClick={() => setCategory(c.value)}
                  title={c.hint}
                >
                  {c.label}
                  <Badge bg="secondary" className="float-end">{sidebarCounts[c.value] || 0}</Badge>
                </Button>
              ))}
            </Card.Body>
          </Card>
        </Col>

        <Col md={9} lg={10}>
          <div className="mb-3 position-relative">
            <FaSearch
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
              }}
            />
            <Form.Control
              type="search"
              placeholder="Search documents..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-dark text-light border-secondary ps-5"
            />
          </div>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragging ? ACCENT : '#404040'}`,
              borderRadius: 10,
              padding: '28px 16px',
              textAlign: 'center',
              color: '#9ca3af',
              background: dragging ? 'rgba(96,165,250,0.08)' : '#1a1a1a',
              cursor: 'pointer',
              marginBottom: 16,
              transition: 'border-color 0.15s, background 0.15s',
            }}
          >
            <FaUpload className="mb-2" style={{ fontSize: 22, color: ACCENT }} />
            <div style={{ fontWeight: 600, color: '#e5e7eb' }}>
              Drop files here or click to upload
            </div>
            <div style={{ fontSize: 13 }}>PDF, Word, Excel, images, zip…</div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : documents.length === 0 ? (
            <Card className="bg-dark text-light border-secondary">
              <Card.Body className="text-center py-5" style={{ color: '#9ca3af' }}>
                No documents in this folder yet.
                <div className="mt-2 small">
                  Try uploading signed contracts, offer PDFs, NDAs, or blank templates.
                </div>
              </Card.Body>
            </Card>
          ) : viewMode === 'grid' ? (
            <Row className="g-3">
              {documents.map((doc) => (
                <Col key={doc.id} xs={12} sm={6} lg={4} xl={3}>
                  <Card className="bg-dark text-light border-secondary h-100">
                    <Card.Body>
                      <div className="d-flex align-items-start gap-2 mb-2">
                        <div style={{ fontSize: 28, lineHeight: 1 }}>
                          {fileIcon(doc.file_ext, doc.mime_type)}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={doc.title}
                          >
                            {doc.title}
                          </div>
                          <small style={{ color: '#9ca3af' }}>
                            {doc.category_label} · {doc.size_label}
                          </small>
                        </div>
                      </div>
                      {doc.company_name ? (
                        <div className="mb-2">
                          <Badge bg="secondary">{doc.company_name}</Badge>
                        </div>
                      ) : null}
                      <div style={{ color: '#9ca3af', fontSize: 12 }} className="mb-3">
                        {moment(doc.updated_at).format('DD MMM YYYY')}
                        {doc.original_filename ? ` · ${doc.original_filename}` : ''}
                      </div>
                      <div className="d-flex gap-1 flex-wrap">
                        {doc.file_url && (
                          <Button
                            as="a"
                            href={doc.file_url}
                            target="_blank"
                            rel="noreferrer"
                            size="sm"
                            variant="outline-primary"
                            style={{ borderColor: ACCENT, color: ACCENT }}
                          >
                            <FaDownload className="me-1" /> Open
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            setEditing({
                              ...doc,
                              company: doc.company ? String(doc.company) : '',
                              newFile: null,
                            });
                            setShowEdit(true);
                          }}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(doc)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Card className="bg-dark text-light border-secondary">
              <Table hover variant="dark" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Company</th>
                    <th>Size</th>
                    <th>Updated</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span style={{ fontSize: 18 }}>
                            {fileIcon(doc.file_ext, doc.mime_type)}
                          </span>
                          <div>
                            <div style={{ fontWeight: 600 }}>{doc.title}</div>
                            <small style={{ color: '#9ca3af' }}>{doc.original_filename}</small>
                          </div>
                        </div>
                      </td>
                      <td>{doc.category_label}</td>
                      <td style={{ color: '#9ca3af' }}>{doc.company_name || '—'}</td>
                      <td>{doc.size_label}</td>
                      <td>{moment(doc.updated_at).format('DD-MM-YYYY')}</td>
                      <td className="text-end">
                        {doc.file_url && (
                          <Button
                            as="a"
                            href={doc.file_url}
                            target="_blank"
                            rel="noreferrer"
                            size="sm"
                            variant="outline-primary"
                            className="me-1"
                            style={{ borderColor: ACCENT, color: ACCENT }}
                          >
                            <FaDownload />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="me-1"
                          onClick={() => {
                            setEditing({
                              ...doc,
                              company: doc.company ? String(doc.company) : '',
                              newFile: null,
                            });
                            setShowEdit(true);
                          }}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(doc)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}
        </Col>
      </Row>

      <Modal show={showUpload} onHide={() => setShowUpload(false)} centered contentClassName="bg-dark text-light">
        <Modal.Header closeButton closeVariant="white" style={{ borderBottom: '1px solid #333' }}>
          <Modal.Title>Upload document</Modal.Title>
        </Modal.Header>
        <Form onSubmit={submitUpload}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>File</Form.Label>
              <Form.Control
                type="file"
                required={!uploadForm.file}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setUploadForm((p) => ({
                    ...p,
                    file: f,
                    title: p.title || f.name.replace(/\.[^.]+$/, ''),
                  }));
                }}
                className="bg-dark text-light border-secondary"
              />
              {uploadForm.file ? (
                <Form.Text style={{ color: '#9ca3af' }}>{uploadForm.file.name}</Form.Text>
              ) : null}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                required
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                className="bg-dark text-light border-secondary"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={uploadForm.category}
                onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                className="bg-dark text-light border-secondary"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Company (optional)</Form.Label>
              <Form.Select
                value={uploadForm.company}
                onChange={(e) => setUploadForm({ ...uploadForm, company: e.target.value })}
                className="bg-dark text-light border-secondary"
              >
                <option value="">None</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-1">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                className="bg-dark text-light border-secondary"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: '1px solid #333' }}>
            <Button variant="secondary" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button
              type="submit"
              disabled={uploading}
              style={{ backgroundColor: ACCENT, borderColor: ACCENT }}
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showEdit}
        onHide={() => {
          setShowEdit(false);
          setEditing(null);
        }}
        centered
        contentClassName="bg-dark text-light"
      >
        <Modal.Header closeButton closeVariant="white" style={{ borderBottom: '1px solid #333' }}>
          <Modal.Title>Edit document</Modal.Title>
        </Modal.Header>
        {editing && (
          <Form onSubmit={submitEdit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  required
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="bg-dark text-light border-secondary"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Company</Form.Label>
                <Form.Select
                  value={editing.company}
                  onChange={(e) => setEditing({ ...editing, company: e.target.value })}
                  className="bg-dark text-light border-secondary"
                >
                  <option value="">None</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={editing.description || ''}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
              <Form.Group className="mb-1">
                <Form.Label>Replace file (optional)</Form.Label>
                <Form.Control
                  type="file"
                  onChange={(e) => setEditing({ ...editing, newFile: e.target.files?.[0] || null })}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer style={{ borderTop: '1px solid #333' }}>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEdit(false);
                  setEditing(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" style={{ backgroundColor: ACCENT, borderColor: ACCENT }}>
                Save
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>
    </Container>
  );
};

export default Documents;
